import {skipToken} from '@reduxjs/toolkit/query';
import {
  Action,
  Alert,
  DataStatus,
  Dropdown,
  DropdownItem,
  useTranslationContext,
} from 'platform/components';
import {Grid, Heading, Hide, HStack, Link, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency, useDateTimeFormatter} from 'platform/locale';

import {useState} from 'react';

import {concat, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetTenantQuery, useSourcingAllowedCountryAndCurrencyQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {
  useCreateInterestPriceReportMutation,
  useCreateInterestPriceReportPreviewQuery,
  useGetInterestPriceReportsQuery,
} from '@dms/api/finance';
import {
  useCreatePriceReportRequestMutation,
  useCreateVehiclePriceReportMutation,
  useCreateVehiclePriceReportPreviewQuery,
  useGetVehiclePriceReportsQuery,
} from '@dms/api/sales';
import {getApiErrorMessages} from '@dms/api/utils';

import {
  buildArray,
  EMPTY_PLACEHOLDER,
  Nullish,
  parseDate,
  suffixTestId,
  TestIdProps,
} from 'shared/web';

import {DEFAULT_COUNTRY} from '../../constants/country';
import {useCountry} from '../../hooks/useCountry';
import {getNaturalPersonFullName} from '../../utils/getNaturalPersonFullName';
import {handleApiError} from '../../utils/handleApiError';
import {OlderReportRow} from '../OlderReportsList/OlderReportRow';
import {OlderReportsList} from '../OlderReportsList/OlderReportsList';
import {Widget} from '../Widget/Widget';
import {AttractivenessBar} from './components/AttractivenessBar';
import {PriceReportWidgetLogo} from './components/PriceReportWidgetLogo';

export type PriceReportWidgetContext = 'vehicle' | 'interest';

interface PriceReportWidgetProps extends TestIdProps {
  resourceId: string | Nullish;
  vehicleType: string | Nullish;
  context: PriceReportWidgetContext;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function PriceReportWidget(props: PriceReportWidgetProps) {
  const formatDateTime = useDateTimeFormatter();
  const formatCurrency = useFormatCurrency();
  const t = useTranslationContext();
  const [error, setError] = useState<string | string[]>([]);
  const [createVehiclePriceReport, {isLoading: isCreateVehiclePriceReportLoading}] =
    useCreateVehiclePriceReportMutation();
  const [createPriceReportRequest, {isLoading: isCreatePriceReportRequestLoading}] =
    useCreatePriceReportRequestMutation();
  const [createInterestPriceReport, {isLoading: isCreateInterestPriceReportLoading}] =
    useCreateInterestPriceReportMutation();
  const {data} = useSourcingAllowedCountryAndCurrencyQuery();
  const {data: tenant} = useGetTenantQuery();
  const {getCountryName} = useCountry();
  const [country, setCountry] = useState<string>(tenant?.country ?? DEFAULT_COUNTRY);

  const isPriceReportAvailable =
    isNotNil(props.resourceId) &&
    ['VEHICLETYPE_PASSENGER_CAR', 'VEHICLETYPE_VAN'].includes(props.vehicleType ?? '');
  const isInterestContext = props.context === 'interest';

  const {data: vehiclePriceReports = [], isLoading: isVehiclePriceReportsLoading} =
    useGetVehiclePriceReportsQuery(
      isNotNil(props.resourceId) ? {vehicleId: props.resourceId} : skipToken,
      {skip: isInterestContext || !isPriceReportAvailable}
    );

  const {
    data: vehiclePriceReportPreview,
    isFetching: isVehiclePriceReportPreviewFetching,
    isError: isVehiclePriceReportPreviewError,
  } = useCreateVehiclePriceReportPreviewQuery(
    isNotNil(props.resourceId)
      ? {vehicleId: props.resourceId, createVehiclePriceReportRequestBody: {country}}
      : skipToken,
    {skip: isInterestContext || !isPriceReportAvailable}
  );
  const {
    data: interestPriceReportPreview,
    isFetching: isInterestPriceReportPreviewFetching,
    isError: isInterestPriceReportPreviewError,
  } = useCreateInterestPriceReportPreviewQuery(
    isNotNil(props.resourceId)
      ? {interestId: props.resourceId, createVehiclePriceReportRequestBody: {country}}
      : skipToken,
    {skip: !isInterestContext || !isPriceReportAvailable}
  );
  const soldVehiclesCount =
    vehiclePriceReportPreview?.sold ?? interestPriceReportPreview?.sold ?? 0;
  const stockVehiclesCount =
    vehiclePriceReportPreview?.stock ?? interestPriceReportPreview?.stock ?? 0;
  const similarVehiclesCount = soldVehiclesCount + stockVehiclesCount;

  const {data: interestPriceReports = [], isLoading: isInterestPriceReportsLoading} =
    useGetInterestPriceReportsQuery(
      {interestId: props.resourceId!},
      {skip: !isInterestContext || !isPriceReportAvailable}
    );

  const priceReports = concat(vehiclePriceReports, interestPriceReports);

  const isDataLoading = isVehiclePriceReportsLoading || isInterestPriceReportsLoading;
  const isCreateLoading =
    isCreateVehiclePriceReportLoading ||
    isCreatePriceReportRequestLoading ||
    isCreateInterestPriceReportLoading;

  const [newestReport] = priceReports;

  const olderReports = priceReports.slice(1);

  const hasReportDetails =
    isNotNil(newestReport?.sellingPrice) ||
    isNotNil(newestReport?.purchasePrice) ||
    isNotNil(newestReport?.turnover) ||
    isNotNil(newestReport?.attractivenessScore);

  const {data: user} = useGetUserQuery(
    {id: newestReport?.createdBy ?? ''},
    {skip: isNilOrEmpty(newestReport?.createdBy) || !isPriceReportAvailable}
  );

  if (!isPriceReportAvailable) {
    return null;
  }

  const userFullName = getNaturalPersonFullName({
    firstName: user?.firstName,
    lastName: user?.lastName,
  });

  const errors = Array.isArray(error) ? error : [error];

  const getCreateReportMutation = () => {
    const resourceId = props.resourceId;
    if (isNil(resourceId)) {
      throw new Error('Resource ID is required');
    }
    if (isInterestContext) {
      return createInterestPriceReport({interestId: resourceId, body: {country}}).unwrap();
    }
    if (similarVehiclesCount > 0) {
      return createVehiclePriceReport({vehicleId: resourceId, body: {country}}).unwrap();
    }
    return createPriceReportRequest({vehicleId: resourceId, body: {country}}).unwrap();
  };

  const onCreateReport = () =>
    getCreateReportMutation()
      .then((data) => window.open(data.url))
      .catch((err: Error) =>
        handleApiError(err, {
          silent: true,
          callback: ({error}) => setError(getApiErrorMessages(error)),
        })
      );

  const widgetActions = buildArray<Action>()
    .when(isNotNil(newestReport), {
      type: 'button',
      title: t('entity.priceReport.viewResults'),
      variant: 'secondary',
      rightIcon: 'action/launch',
      onClick: () => window.open(newestReport!.url),
      'data-testid': suffixTestId('viewResults', props),
    })
    .when(isNotNil(newestReport), {
      type: 'button',
      title: t('entity.priceReport.newReport'),
      variant: 'secondary',
      onClick: onCreateReport,
      isLoading: isCreateLoading,
      'data-testid': suffixTestId('newReportButton', props),
    })
    .when(isNil(newestReport), {
      type: 'button',
      title: t('entity.priceReport.getReport'),
      variant: 'secondary',
      onClick: onCreateReport,
      isLoading: isCreateLoading,
      'data-testid': suffixTestId('getReportButton', props),
    });

  return (
    <VStack spacing={2}>
      <Widget.Card data-testid={suffixTestId('priceReportWidget', props)}>
        <Widget.Header
          logo={<PriceReportWidgetLogo data-testid={props['data-testid']} />}
          title={t('entity.priceReport.title')}
          headerActions={
            <HStack align="center" spacing={2}>
              <Text size="xSmall" color="secondary">
                {t('entity.priceReport.market')}
              </Text>
              <Dropdown
                data-testid={suffixTestId('countryDropdown', props)}
                dropdownControl={
                  <Link
                    data-testid={suffixTestId('countryLink', props)}
                    size="small"
                    title={getCountryName(country)}
                    rightIcon="hardware/keyboard_arrow_down"
                  />
                }
              >
                {data?.countries?.map((code, index) => (
                  <DropdownItem
                    key={code}
                    data-testid={suffixTestId(`country-item[${index}]`, props)}
                    label={getCountryName(code)}
                    onClick={() => setCountry(code)}
                  />
                ))}
              </Dropdown>
            </HStack>
          }
          subtitle={
            <DataStatus isLoading={isDataLoading}>
              <Show when={newestReport}>
                <HStack spacing={3}>
                  <Text
                    size="xSmall"
                    color="tertiary"
                    data-testid={suffixTestId('recentReport', props)}
                  >
                    {`${t('entity.priceReport.recentReport')} ${
                      newestReport?.createdAt
                        ? formatDateTime('dateShort', parseDate(newestReport.createdAt))
                        : ''
                    }`}
                    {isNotNilOrEmpty(userFullName) ? `, ${userFullName}` : ''}
                  </Text>
                  <Show when={isNotNil(newestReport?.country)}>
                    <Text
                      size="xSmall"
                      color="secondary"
                      data-testid={suffixTestId('recentReport-country', props)}
                    >
                      ({newestReport?.country})
                    </Text>
                  </Show>
                </HStack>
              </Show>
              <Hide when={newestReport}>
                <Text size="xSmall" color="secondary">
                  {t('entity.priceReport.description')}
                </Text>
              </Hide>
            </DataStatus>
          }
          actions={widgetActions}
          data-testid={suffixTestId('priceReportWidget', props)}
        />

        <Show when={hasReportDetails}>
          <Widget.Content data-testid={suffixTestId('priceReportWidget', props)}>
            <VStack spacing={4}>
              <Grid columns={[1, 1, 3, 3]}>
                <VStack spacing={1}>
                  <Text
                    size="xSmall"
                    color="secondary"
                    data-testid={suffixTestId('sellingPrice-label', props)}
                  >
                    {t('entity.priceReport.realSellingPrice')}
                  </Text>
                  <Heading size={4} data-testid={suffixTestId('sellingPrice-value', props)}>
                    {isNotNil(newestReport) &&
                    isNotNil(newestReport.sellingPrice) &&
                    isNotNil(newestReport.sellingPriceCurrency)
                      ? formatCurrency(newestReport.sellingPrice, newestReport.sellingPriceCurrency)
                      : EMPTY_PLACEHOLDER}
                  </Heading>
                </VStack>
                <VStack spacing={1}>
                  <Text
                    size="xSmall"
                    color="secondary"
                    data-testid={suffixTestId('purchasePrice-label', props)}
                  >
                    {t('entity.priceReport.recommendedPurchasePrice')}
                  </Text>
                  <Heading size={4} data-testid={suffixTestId('purchasePrice-value', props)}>
                    {isNotNil(newestReport) &&
                    isNotNil(newestReport.purchasePrice) &&
                    isNotNil(newestReport.purchasePriceCurrency)
                      ? formatCurrency(
                          newestReport.purchasePrice,
                          newestReport.purchasePriceCurrency
                        )
                      : EMPTY_PLACEHOLDER}
                  </Heading>
                </VStack>
                <VStack spacing={1}>
                  <Text
                    size="xSmall"
                    color="secondary"
                    data-testid={suffixTestId('turnover-label', props)}
                  >
                    {t('entity.priceReport.expectedSaleTime')}
                  </Text>
                  <Heading size={4} data-testid={suffixTestId('turnover-value', props)}>
                    {isNotNil(newestReport) && isNotNil(newestReport.turnover)
                      ? t('entity.priceReport.days', {count: Math.round(newestReport.turnover)})
                      : EMPTY_PLACEHOLDER}
                  </Heading>
                </VStack>
              </Grid>
              <AttractivenessBar
                value={newestReport?.attractivenessScore}
                title={t('entity.priceReport.vehicleAttractiveness')}
                lowLabel={t('entity.priceReport.blacklist')}
                highLabel={t('entity.priceReport.hitlist')}
                data-testid={suffixTestId('priceReportWidget', props)}
              />
            </VStack>
          </Widget.Content>
        </Show>

        <Show when={olderReports.length > 0}>
          <OlderReportsList
            title={t('entity.priceReport.previousReports')}
            data-testid={props['data-testid']}
          >
            {olderReports.map((report, index) => (
              <OlderReportRow
                key={report.id ?? index}
                href={report.url}
                title={t('entity.priceReport.previousReport', {
                  date: report.createdAt
                    ? formatDateTime('dateShort', parseDate(report.createdAt))
                    : '',
                })}
                index={index}
                createdBy={report.createdBy}
                meta={
                  <Show when={isNotNil(report.country)}>
                    <Text size="small" color="primary">
                      ({report.country})
                    </Text>
                  </Show>
                }
                data-testid={suffixTestId(`previousReport-[${index}]`, props)}
              />
            ))}
          </OlderReportsList>
        </Show>
      </Widget.Card>

      <Hide when={isVehiclePriceReportPreviewError || isInterestPriceReportPreviewError}>
        <DataStatus
          isLoading={isVehiclePriceReportPreviewFetching || isInterestPriceReportPreviewFetching}
          minHeight={9}
        >
          <Alert
            type="inline"
            variant={similarVehiclesCount === 0 ? 'warning' : 'info'}
            message={
              similarVehiclesCount === 0
                ? t('entity.priceReport.noSimilarVehicles')
                : t('entity.priceReport.preview', {
                    vehicleCount: similarVehiclesCount,
                  })
            }
          />
        </DataStatus>
      </Hide>
      {errors.map((error, index) => (
        <Alert
          data-testid={suffixTestId(`errorAlert[${index}]`, props)}
          key={error}
          type="inline"
          variant="warning"
          message={error}
        />
      ))}
    </VStack>
  );
}
